// @ts-nocheck
import * as React from 'react';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import {
  Navigate,
  useSearchParams,
  useNavigate,
  useLocation,
  Link,
} from 'react-router-dom';

import { useLogin } from '../data/useLogin';
import { useLoginOAuthAuthorize } from '../data/useLoginOAuthAuthorize';
import { useRecaptcha } from '../data/useRecaptcha';
import { useAccount, useAuth } from '../auth';
import { initiateGatehouseLogin, isGatehouseEnabled } from '../auth/gatehouse-oauth';

interface QueryError {
  response?: {
    data?: {
      detail?: string;
      title?: string;
    };
  };
}

interface Query {
  error?: QueryError;
}

const getAlertFromQuery = (query: Query) => {
  const errorData = query.error?.response?.data;

  let errorText = 'Unknown Error';
  if (errorData) {
    errorText = errorData.detail || errorData.title;
  }

  return <Alert severity="error">{errorText}</Alert>;
};

export const Login = () => {
  const [rerendered, setRerender] = React.useState(0);
  const [searchParams] = useSearchParams();
  const [provider, setProvider] = React.useState('email');
  const [showOAuthMessage, setShowOAuthMessage] = React.useState(false);

  const loginMutation = useLogin();
  const { executeRecaptcha } = useRecaptcha();
  const [email, setEmail] = React.useState();
  const [emailValid, setEmailValid] = React.useState(true);
  const [password, setPassword] = React.useState();
  const [passwordValid, setPasswordValid] = React.useState(true);
  const { currentAccount } = useAccount();
  const location = useLocation();
  const auth = useAuth();
  // TODO: pass path to redirect to
  const authorizeQuery = useLoginOAuthAuthorize(provider);
  const navigate = useNavigate();
  if (provider != 'email' && authorizeQuery.isSuccess) {
    setShowOAuthMessage(true);
    window.location = authorizeQuery.data.authorize_url;
  }

  // Show OAuth message when provider is selected (for testing)
  React.useEffect(() => {
    if (provider !== 'email') {
      setShowOAuthMessage(true);
    }
  }, [provider]);

  React.useEffect(() => {
    // rerender on login success so that the user query is re-ran
    // TODO: get url from query or local state
    if (loginMutation.isSuccess && rerendered < 1) {
      setRerender(rerendered + 1);
    }
    if (loginMutation.isSuccess && currentAccount?.name) {
      navigate(`/${currentAccount?.name}/flows`);
      // TODO: get url from query or local state
      // return <Navigate to="/" replace />;
      return;
    }
  }, [loginMutation.isSuccess, currentAccount, rerendered]);

  let error = null;
  const qsError = searchParams.get('error_description');
  if (qsError) {
    error = (
      <Alert data-cy="error-message" severity="error">
        {qsError}
      </Alert>
    );
  } else if (loginMutation.isError) {
    error = getAlertFromQuery(loginMutation);
  } else if (authorizeQuery.isError) {
    error = getAlertFromQuery(authorizeQuery);
  }

  const validate = () => {
    const emailValidation = email && email.length > 0;
    setEmailValid(emailValidation);
    const passwordValidation = password && password.length > 0;
    setPasswordValid(passwordValidation);
    return emailValidation && passwordValidation;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const valid = validate();

    if (valid) {
      const token = await executeRecaptcha('login');
      loginMutation.mutate({ email, password, recaptcha_response: token });
    }
  };

  return (
    <div
      style={{
        background: 'linear-gradient(#74C9E6, #3174AB)', // no-repeat bottom url("./assets/images/login-bg.svg"),
        backgroundSize: 'cover',
        height: '100%',
        minHeight: '100vh',
        display: 'block',
        paddingBottom: '1em',
      }}
    >
      <div
        style={{
          background: '#ffffff',
          padding: '1em',
        }}
      >
        <img
          src="assets/images/aquifer-logo-rgb.svg"
          alt="Aquifer"
          data-cy="aquifer-logo"
          style={{
            margin: '0 auto',
            display: 'block',
            width: '255px',
          }}
        />
        {window.location.search.includes('debug') && (
          <small style={{ margin: '0 auto', display: 'block', width: '100px' }}>
            UI:
            <a
              style={{ paddingLeft: '5px' }}
              href={`https://github.com/aquifer-cloud/aquifer-frontend/commit/${process.env.NX_PUBLIC_COMMIT_HASH}`}
            >
              {process.env.NX_PUBLIC_COMMIT_HASH}
            </a>
          </small>
        )}
      </div>

      <div
        style={{
          textAlign: 'center',
          color: '#ffffff',
          fontSize: '3em',
          margin: '1.25em 0',
        }}
      >
        Get your data flowing
      </div>

      <Card
        style={{
          width: '250px',
          position: 'relative',
          margin: '0 auto',
          padding: '2em 3.5em',
        }}
      >
        <Backdrop
          sx={{
            background: 'rgba(255, 255, 255, 0.5)',
            position: 'absolute',
            zIndex: (theme) => theme.zIndex.drawer + 1,
          }}
          open={loginMutation.isLoading || authorizeQuery.isLoading}
        >
          <CircularProgress />
        </Backdrop>
        <Stack spacing={2}>
          <div
            style={{
              fontFamily: 'Noto Sans',
              fontSize: '24px',
              lineHeight: '30px',
              textAlign: 'center',
              fontWeight: 'bold',
              textTransform: 'uppercase',
            }}
            data-cy="login-title"
          >
            Login
          </div>

          {error}

          {showOAuthMessage && (
            <Alert data-cy="oauth-message" severity="info">
              {provider === 'microsoft'
                ? 'Redirecting to Microsoft...'
                : 'Redirecting to Google...'}
            </Alert>
          )}

          <Button
            variant="contained"
            onClick={() => setProvider('microsoft')}
            data-cy="microsoft-button"
            style={{
              backgroundColor: '#ffffff',
              color: '#000000',
              textTransform: 'none',
              textAlign: 'left',
            }}
          >
            <img
              src="assets/images/microsoft.svg"
              alt="Microsoft"
              style={{
                width: '25px',
                height: '25px',
                margin: '0.5em',
              }}
            />
            Sign In With Microsoft
          </Button>

          <Button
            variant="contained"
            onClick={() => setProvider('google')}
            data-cy="google-button"
            style={{
              backgroundColor: '#ffffff',
              color: '#000000',
              textTransform: 'none',
              textAlign: 'left',
            }}
          >
            <img
              src="assets/images/google.png"
              alt="Google"
              style={{
                width: '25px',
                height: '25px',
                margin: '0.5em',
              }}
            />
            Sign In With Google
          </Button>

          {isGatehouseEnabled() && (
            <Button
              variant="contained"
              onClick={() => initiateGatehouseLogin('/')}
              data-cy="gatehouse-button"
              style={{
                backgroundColor: '#0f172a',
                color: '#ffffff',
                textTransform: 'none',
                textAlign: 'left',
                padding: '0.5em 1em',
              }}
            >
              Sign In With Aquifer Cloud
            </Button>
          )}

          <div
            style={{
              fontFamily: 'Noto Sans',
              fontSize: '12px',
              lineHeight: '30px',
              textAlign: 'center',
              textTransform: 'uppercase',
            }}
          >
            Or
          </div>

          <form onSubmit={handleSubmit}>
            <Stack spacing={2}>
              <TextField
                data-cy="email"
                label="Email"
                placeholder="Email"
                value={email || ''}
                onChange={(e) => setEmail(e.target.value)}
                error={!emailValid}
                helperText={!emailValid ? 'Email Required' : ''}
              />

              <TextField
                data-cy="password"
                label="Password"
                placeholder="Password"
                type="password"
                autoComplete="current-password"
                value={password || ''}
                onChange={(e) => setPassword(e.target.value)}
                error={!passwordValid}
                helperText={!passwordValid ? 'Password Required' : ''}
              />

              <Button
                data-cy="sign-in-button"
                type="submit"
                variant="contained"
              >
                Sign In
              </Button>
            </Stack>
          </form>

          <Link
            to="/forgot-password"
            style={{
              textAlign: 'center',
              color: '#3174AB',
              textDecoration: 'none',
              fontSize: '14px',
              display: 'block',
            }}
            data-cy="forgot-password-link"
          >
            Forgot password?
          </Link>
        </Stack>
      </Card>
      <div
        style={{
          width: 360,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          margin: '0 auto',
          padding: '1em',
          color: '#fff',
        }}
      >
        <div style={{ display: 'flex' }}>
          <Link style={{ color: '#fff' }} to="/privacy-policy">
            Privacy Policy
          </Link>
          <span style={{ margin: '0 0.5em' }}>|</span>
          <Link style={{ color: '#fff' }} to="/terms-of-service">
            Terms of Service
          </Link>
        </div>
        <small
          style={{
            marginTop: '0.5em',
            fontSize: '11px',
            opacity: 0.8,
            textAlign: 'center',
          }}
        >
          This site is protected by reCAPTCHA and the Google{' '}
          <a
            href="https://policies.google.com/privacy"
            style={{ color: '#fff' }}
          >
            Privacy Policy
          </a>{' '}
          and{' '}
          <a href="https://policies.google.com/terms" style={{ color: '#fff' }}>
            Terms of Service
          </a>{' '}
          apply.
        </small>
      </div>
    </div>
  );
};
